import { useState, useEffect } from "react";
import { Lock, Eye, EyeOff } from "lucide-react";

// Acesso restrito temporário ao site público.
// Credenciais lidas de variáveis de ambiente (build-time). Definir em frontend/.env:
//   REACT_APP_SITE_GATE_EMAIL=...
//   REACT_APP_SITE_GATE_PASSWORD=...
// Para desativar o gate: definir REACT_APP_SITE_GATE_DISABLED=true
const GATE_EMAIL = process.env.REACT_APP_SITE_GATE_EMAIL || "";
const GATE_PASSWORD = process.env.REACT_APP_SITE_GATE_PASSWORD || "";
const GATE_DISABLED = process.env.REACT_APP_SITE_GATE_DISABLED === "true";
const STORAGE_KEY = "caseirinho_site_access";
const STORAGE_VALUE = "granted_2026";

export default function SiteGate({ children }) {
    const [granted, setGranted] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.localStorage.getItem(STORAGE_KEY) === STORAGE_VALUE;
    });

    // Disabled entirely via env flag — useful for staging without restriction
    if (GATE_DISABLED || !GATE_EMAIL || !GATE_PASSWORD) return children;

    // Bypass: admin/vendor/client dashboards + login flow — usam autenticação própria
    if (typeof window !== "undefined") {
        const path = window.location.pathname;
        const bypassed = [
            "/admin", "/vendor", "/conta",
            "/login", "/register", "/forgot-password", "/reset-password",
            "/fornecedores/registo", "/r/",
            "/tracking", "/track",
        ];
        if (bypassed.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p))) {
            return children;
        }
    }

    if (granted) return children;
    return <GateScreen onUnlock={() => setGranted(true)} />;
}

function GateScreen({ onUnlock }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = ""; };
    }, []);

    function submit(e) {
        e.preventDefault();
        setError("");
        setLoading(true);
        setTimeout(() => {
            if (email.trim().toLowerCase() === GATE_EMAIL && password === GATE_PASSWORD) {
                window.localStorage.setItem(STORAGE_KEY, STORAGE_VALUE);
                onUnlock();
            } else {
                setError("Credenciais inválidas. Acesso negado.");
            }
            setLoading(false);
        }, 400);
    }

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-elegant" data-testid="site-gate">
            {/* Background grain */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "20px 20px" }} />

            <div className="relative w-full max-w-md mx-6">
                <div className="text-center mb-10">
                    <span className="label-overline text-gold-soft tracking-[0.4em]">Acesso Restrito</span>
                    <h1 className="font-heading text-4xl text-cream mt-4 leading-tight">
                        Caseirinho.pt
                    </h1>
                    <p className="text-sm text-cream/55 mt-3 max-w-xs mx-auto">
                        Esta plataforma está em fase privada. Introduza as credenciais para aceder.
                    </p>
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <label className="block">
                        <span className="label-overline text-gold-soft/70 text-[10px]">E-mail</span>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                            className="mt-2 w-full bg-cream/5 border border-cream/20 rounded-md px-4 py-3 text-cream text-sm focus:border-gold-soft focus:outline-none transition-colors"
                            data-testid="gate-email"
                        />
                    </label>

                    <label className="block">
                        <span className="label-overline text-gold-soft/70 text-[10px]">Senha</span>
                        <div className="relative mt-2">
                            <input
                                type={show ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                autoComplete="current-password"
                                className="w-full bg-cream/5 border border-cream/20 rounded-md px-4 py-3 pr-10 text-cream text-sm focus:border-gold-soft focus:outline-none transition-colors"
                                data-testid="gate-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShow((v) => !v)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-cream/40 hover:text-gold-soft"
                                tabIndex={-1}
                            >
                                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </div>
                    </label>

                    {error && (
                        <p className="text-destructive text-xs label-overline" data-testid="gate-error">{error}</p>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gold-soft text-wine-dark font-semibold py-3 rounded-md hover:bg-gold-deep hover:text-cream transition-all label-overline tracking-[0.3em] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                        data-testid="gate-submit"
                    >
                        <Lock className="h-3.5 w-3.5" />
                        {loading ? "A verificar…" : "Entrar"}
                    </button>
                </form>

                <p className="text-center text-[10px] text-cream/30 mt-10 label-overline tracking-[0.3em]">
                    Caseirinho · Vinhos & Sabores Portugueses
                </p>
            </div>
        </div>
    );
}
